import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe, ArrowRight, MapPin, Mail, Phone, ChevronDown, User } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { EditableText } from "@/components/cms/CmsText";
import { HOTELS } from "@/lib/data";
import MenuPropertyPreview from "@/components/MenuPropertyPreview";
import SitePreferences from "@/components/SitePreferences";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const Header = () => {
  const { t, lang, setLang, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set()); // expanded accordion item `to` values
  const location = useLocation();

  // Close drawer + collapse accordions on route change.
  useEffect(() => {
    setOpen(false);
    setExpanded(new Set());
  }, [location.pathname]);

  // Lock body scroll while drawer is open.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Auto-discover property lists for drawer accordions. Bivouacs share a
  // distinct sub-page (/bivouacs); everything else (hotels + refuges) is
  // grouped under "Hotels & Properties".
  const hotelProperties = HOTELS.filter((h) => h.category !== "bivouac");
  const bivouacProperties = HOTELS.filter((h) => h.category === "bivouac");

  const toLocale = (name) => ({ en: name, fr: name, es: name }); // hotel names aren't translated

  // Categorised navigation — only Home + Book CTA stay in the top bar.
  const SECTIONS = [
    {
      id: "stay",
      title: { en: "Stay", fr: "Séjourner", es: "Alojamientos" },
      lead: {
        en: "Charming hotels and bivouacs in the desert.",
        fr: "Hôtels de charme et bivouacs dans le désert.",
        es: "Hoteles con encanto y bivouacs en el desierto.",
      },
      items: [
        {
          to: "/hotels",
          label: { en: "Hotels", fr: "Hôtels", es: "Hoteles" },
          children: [
            {
              to: "/hotels",
              label: { en: "All hotels", fr: "Tous les hôtels", es: "Todos los hoteles" },
              meta: { en: "Index", fr: "Index", es: "Índice" },
            },
            ...hotelProperties.map((h) => ({
              to: `/hotels/${h.slug}`,
              label: toLocale(h.name),
              meta: { en: h.location || "", fr: h.location || "", es: h.location || "" },
              hotel: h,
            })),
          ],
        },
        {
          to: "/bivouacs",
          label: { en: "Bivouacs", fr: "Bivouacs", es: "Bivouacs" },
          children: [
            {
              to: "/bivouacs",
              label: { en: "All bivouacs", fr: "Tous les bivouacs", es: "Todos los bivouacs" },
              meta: { en: "Index", fr: "Index", es: "Índice" },
            },
            ...bivouacProperties.map((b) => ({
              to: `/hotels/${b.slug}`,
              label: toLocale(b.name),
              meta: { en: b.location || "", fr: b.location || "", es: b.location || "" },
              hotel: b,
            })),
          ],
        },
      ],
    },
    {
      id: "book",
      title: { en: "Book", fr: "Réserver", es: "Reservar" },
      lead: {
        en: "Book directly with our team.",
        fr: "Réservez directement avec notre équipe.",
        es: "Reserva directamente con nuestro equipo.",
      },
      items: [
        { to: "/booking", label: { en: "Book", fr: "Réserver", es: "Reservar" } },
        { to: "/contact", label: { en: "Contact", fr: "Contact", es: "Contacto" } },
      ],
    },
    {
      id: "discover",
      title: { en: "Discover", fr: "Découvrir", es: "Descubrir" },
      lead: {
        en: "Routes, essential landmarks and the south of Morocco.",
        fr: "Itinéraires, sites incontournables et le sud du Maroc.",
        es: "Rutas, lugares imprescindibles y el sur de Marruecos.",
      },
      items: [
        { to: "/southern-morocco", label: { en: "Morocco", fr: "Maroc", es: "Marruecos" } },
        { to: "/experiences", label: { en: "Experiences", fr: "Expériences", es: "Experiencias" } },
        { to: "/packs", label: { en: "Collections", fr: "Collections", es: "Colecciones" } },
        { to: "/trips", label: { en: "Journeys", fr: "Voyages", es: "Viajes" } },
      ],
    },
    {
      id: "hospitality",
      title: { en: "Hospitality", fr: "Hospitalité", es: "Hospitalidad" },
      lead: {
        en: "Gastronomy, celebrations and everything that makes our houses unique.",
        fr: "Gastronomie, célébrations et tout ce qui rend nos adresses uniques.",
        es: "Gastronomía, celebraciones y todo lo que hace únicos nuestros alojamientos.",
      },
      items: [
        { to: "/gastronomy", label: { en: "Gastronomy", fr: "Gastronomie", es: "Gastronomía" } },
        { to: "/events", label: { en: "Events", fr: "Événements", es: "Eventos" } },
      ],
    },
    {
      id: "about",
      title: { en: "About us", fr: "À propos", es: "Nosotros" },
      lead: {
        en: "The family, the founders and the story behind the group.",
        fr: "La famille, les fondateurs et l'histoire derrière le groupe.",
        es: "La familia, los fundadores y la historia que hay detrás del grupo.",
      },
      items: [
        { to: "/about", label: { en: "Our story", fr: "Notre histoire", es: "Nuestra historia" } },
        { to: "/wall-of-love", label: { en: "Wall of Love", fr: "Wall of Love", es: "Wall of Love" } },
        { to: "/work-with-us", label: { en: "Work with us", fr: "Travailler avec nous", es: "Trabaja con nosotros" } },
        { to: "/press", label: { en: "Press", fr: "Presse", es: "Prensa" } },
        { to: "/partners", label: { en: "Partners", fr: "Partenaires", es: "Partners" } },
      ],
    },
    {
      id: "resources",
      title: { en: "Resources", fr: "Ressources", es: "Recursos" },
      lead: {
        en: "Read our journal, browse our brochures, and find your way.",
        fr: "Lisez notre journal, parcourez nos catalogues et trouvez votre chemin.",
        es: "Lee nuestro blog, consulta los catálogos y descubre cómo llegar.",
      },
      items: [
        { to: "/blog", label: { en: "Blog", fr: "Blog", es: "Blog" } },
        { to: "/brochures", label: { en: "Brochures", fr: "Catalogues", es: "Catálogos" } },
        { to: "/directions", label: { en: "How to find us", fr: "Nous trouver", es: "Cómo llegar" } },
      ],
    },
  ];

  return (
    <>
      <header
        className="fixed top-9 inset-x-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-[#2C2621]/5"
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-4">
          {/* Left — Brand block: logo + wordmark + tagline (all left-aligned) */}
          <Link
            to="/"
            className="group inline-flex items-center gap-2.5 sm:gap-3 md:gap-4 min-w-0 flex-shrink"
            data-testid="brand-logo"
            data-no-xaluca-logo="true"
            data-cms-scope="global.brand"
            aria-label={pick({ en: "Grup Xaluca — home", fr: "Grup Xaluca — accueil", es: "Grup Xaluca — inicio" }, lang)}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_c24999ff-c14f-412e-baf4-b11c0020fcc2/artifacts/zezo764i_Grup_Xaluca_logo.png.webp"
              alt="Grup Xaluca"
              className="h-8 sm:h-9 md:h-11 w-auto object-contain shrink-0 transition-opacity group-hover:opacity-80"
              loading="eager"
              decoding="async"
              data-testid="header-logo-mark"
              data-cms-role="logo"
            />
            <span className="flex flex-col leading-none min-w-0">
              <span className="font-serif-x text-base sm:text-xl md:text-2xl lg:text-3xl tracking-tight text-[#2C2621] leading-none whitespace-nowrap">
                Grup&nbsp;Xaluca
              </span>
              <span className="mt-1 text-[8px] sm:text-[9px] md:text-[10px] tracking-[0.22em] sm:tracking-[0.28em] uppercase text-[#C16542] block leading-tight">
                <EditableText
                  slot="brand.tagline"
                  defaults={{
                    en: "The Soul of Southern Morocco",
                    fr: "L'âme du sud marocain",
                    es: "El alma del sur de Marruecos",
                  }}
                />
              </span>
            </span>
          </Link>

          {/* Right — Help CTA (desktop) + Menu toggle + Book CTA. Menu sits to the left of the
              terracotta book button on every viewport. CMS edit controls
              now live in the floating cluster (FloatingCmsButtons). */}
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            {/* "Need help?" — circular concierge shortcut. Desktop-only.
                Reads as a filled cream avatar with a subtle user glyph;
                on hover it flips to the brand terracotta to signal the
                shortcut lives in the same family as the Book CTA. */}
            <Link
              to="/contact"
              className="hidden md:inline-flex items-center gap-2.5 group text-[#2C2621] hover:text-[#C16542] transition-colors"
              data-testid="header-help-cta"
              aria-label={pick(
                { en: "Need help? — contact us", fr: "Besoin d'aide ? — nous contacter", es: "¿Necesitas ayuda? — contáctanos" },
                lang
              )}
            >
              <span className="text-[10px] tracking-[0.3em] uppercase">
                {pick(
                  { en: "Need help?", fr: "Besoin d'aide ?", es: "¿Necesitas ayuda?" },
                  lang
                )}
              </span>
              <span className="w-10 h-10 rounded-full bg-[#F2EBE1] group-hover:bg-[#C16542] border border-[#2C2621]/15 group-hover:border-[#C16542] flex items-center justify-center transition-colors">
                <User
                  className="w-4 h-4 text-[#2C2621] group-hover:text-[#FDFBF7] transition-colors"
                  strokeWidth={1.6}
                />
              </span>
            </Link>

            <button
              className="flex items-center gap-2.5 group text-[#2C2621] hover:text-[#C16542] transition-colors"
              onClick={() => setOpen(true)}
              data-testid="menu-toggle"
              aria-label={pick({ en: "Open navigation", fr: "Ouvrir le menu", es: "Abrir el menú" }, lang)}
            >
              <span className="hidden sm:inline text-[10px] tracking-[0.3em] uppercase">
                {pick({ en: "Menu", fr: "Menu", es: "Menú" }, lang)}
              </span>
              <span className="w-10 h-10 rounded-full border border-[#2C2621]/15 group-hover:border-[#C16542] flex items-center justify-center transition-colors">
                <Menu className="w-4 h-4" strokeWidth={1.6} />
              </span>
            </button>
            <Link
              to="/booking"
              className="inline-flex bg-[#C16542] text-[#FDFBF7] px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 text-[10px] md:text-xs tracking-[0.18em] sm:tracking-[0.2em] uppercase hover:bg-[#A35133] transition-colors duration-300"
              data-testid="book-cta-header"
            >
              {t("nav.book")}
            </Link>
          </div>
        </div>
      </header>

      {/* SIDE DRAWER */}
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-[#1A1513]/55 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
        data-testid="nav-drawer-backdrop"
      />

      {/* Drawer panel */}
      <aside
        className={`fixed inset-y-0 left-0 z-[70] w-full sm:max-w-md md:max-w-lg bg-[#FBF6EE] text-[#2C2621] shadow-[24px_0_60px_-20px_rgba(28,22,18,0.55)] transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] flex flex-col ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={pick({ en: "Navigation", fr: "Navigation", es: "Navegación" }, lang)}
        data-testid="nav-drawer"
        data-no-cms-text="true"
        data-no-cms-edit="true"
      >
        {/* Drawer header — features the Grup Xaluca brand mark
            prominently at the top so the brand is the first thing the
            visitor sees when the side nav slides in. Logo + tagline
            sit on the left; close button stays anchored top-right. */}
        <div
          className="flex items-start justify-between gap-4 px-7 md:px-10 pt-7 md:pt-9 pb-6 md:pb-7 border-b border-[#2C2621]/10"
          data-testid="drawer-header"
        >
          <Link
            to="/"
            className="group inline-flex flex-col items-start"
            data-testid="drawer-brand"
            data-no-xaluca-logo="true"
            data-cms-scope="global.brand"
            onClick={() => setOpen(false)}
            aria-label={pick({ en: "Grup Xaluca — home", fr: "Grup Xaluca — accueil", es: "Grup Xaluca — inicio" }, lang)}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_c24999ff-c14f-412e-baf4-b11c0020fcc2/artifacts/zezo764i_Grup_Xaluca_logo.png.webp"
              alt="Grup Xaluca"
              className="h-12 sm:h-14 md:h-16 w-auto object-contain transition-opacity group-hover:opacity-85"
              loading="eager"
              decoding="async"
              data-testid="drawer-brand-logo"
              data-cms-role="logo"
            />
            <span className="mt-3 font-serif-x text-2xl md:text-3xl tracking-tight text-[#2C2621] leading-none">
              Grup&nbsp;Xaluca
            </span>
            <span className="mt-1.5 text-[10px] sm:text-[11px] tracking-[0.28em] uppercase text-[#C16542]">
              {pick(
                {
                  en: "The Soul of Southern Morocco",
                  fr: "L'âme du sud marocain",
                  es: "El alma del sur de Marruecos",
                },
                lang
              )}
            </span>
          </Link>
          <button
            onClick={() => setOpen(false)}
            className="shrink-0 mt-1 w-10 h-10 rounded-full border border-[#2C2621]/15 hover:border-[#C16542] flex items-center justify-center text-[#2C2621] hover:text-[#C16542] transition-colors"
            data-testid="nav-drawer-close"
            aria-label={pick({ en: "Close menu", fr: "Fermer le menu", es: "Cerrar el menú" }, lang)}
          >
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </div>

        {/* Drawer body — scrollable */}
        <div className="flex-1 overflow-y-auto px-7 md:px-10 py-8 md:py-10">
          {/* Home pinned */}
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `block font-serif-x text-3xl md:text-4xl tracking-tight leading-tight pb-7 mb-7 border-b border-[#2C2621]/10 transition-colors ${
                isActive ? "text-[#C16542]" : "text-[#2C2621] hover:text-[#C16542]"
              }`
            }
            data-testid="drawer-link-home"
          >
            {pick({ en: "Home", fr: "Accueil", es: "Inicio" }, lang)}
          </NavLink>

          {SECTIONS.map((sec) => (
            <section key={sec.id} className="mb-9" data-testid={`drawer-section-${sec.id}`}>
              <p className="text-[10px] tracking-[0.35em] uppercase text-[#C16542]">
                {pick(sec.title, lang)}
              </p>
              <p className="mt-2 text-sm text-[#5C5248] leading-relaxed">
                {pick(sec.lead, lang)}
              </p>
              <ul className="mt-5 space-y-3">
                {sec.items.map((it) => {
                  const key = it.to.split("/").filter(Boolean).join("-") || "home";
                  // ── Accordion item (has `children`) ─────────────────
                  if (it.children && it.children.length) {
                    const isOpen = expanded.has(it.to);
                    return (
                      <li key={it.to} data-testid={`drawer-accordion-${key}`}>
                        <button
                          type="button"
                          onClick={() => setExpanded((prev) => {
                            const next = new Set(prev);
                            if (next.has(it.to)) next.delete(it.to);
                            else next.add(it.to);
                            return next;
                          })}
                          aria-expanded={isOpen}
                          aria-controls={`drawer-acc-panel-${key}`}
                          className={`group w-full flex items-center justify-between gap-4 py-1.5 font-serif-x text-2xl md:text-[28px] leading-tight tracking-tight transition-colors ${
                            isOpen ? "text-[#C16542]" : "text-[#2C2621] hover:text-[#C16542]"
                          }`}
                          data-testid={`drawer-accordion-toggle-${key}`}
                        >
                          <span className="text-left">{pick(it.label, lang)}</span>
                          <ChevronDown
                            className={`w-4 h-4 shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                            strokeWidth={1.8}
                          />
                        </button>
                        <div
                          id={`drawer-acc-panel-${key}`}
                          className={`grid transition-all duration-300 ease-out ${isOpen ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0"}`}
                        >
                          <ul className="overflow-hidden border-l border-[#2C2621]/15 pl-4 ml-1 space-y-1.5">
                            {it.children.map((child) => {
                              const childKey = child.to.split("/").filter(Boolean).join("-") || "home";
                              return (
                                <li key={child.to}>
                                  <NavLink
                                    to={child.to}
                                    end
                                    className={({ isActive }) =>
                                      `flex items-baseline justify-between gap-3 py-1.5 text-sm md:text-base transition-colors ${
                                        isActive ? "text-[#C16542]" : "text-[#2C2621]/85 hover:text-[#C16542]"
                                      }`
                                    }
                                    data-testid={`drawer-sublink-${childKey}`}
                                  >
                                    <span className="font-serif-x leading-tight">{pick(child.label, lang)}</span>
                                    {child.meta && pick(child.meta, lang) && (
                                      <span className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248] whitespace-nowrap">
                                        {pick(child.meta, lang)}
                                      </span>
                                    )}
                                  </NavLink>
                                  {child.hotel ? (
                                    <MenuPropertyPreview
                                      hotel={child.hotel}
                                      onNavigate={() => setOpen(false)}
                                    />
                                  ) : null}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </li>
                    );
                  }
                  // ── Plain link item ─────────────────────────────────
                  return (
                    <li key={it.to}>
                      <NavLink
                        to={it.to}
                        className={({ isActive }) =>
                          `group flex items-baseline justify-between gap-4 py-1.5 font-serif-x text-2xl md:text-[28px] leading-tight tracking-tight transition-colors ${
                            isActive ? "text-[#C16542]" : "text-[#2C2621] hover:text-[#C16542]"
                          }`
                        }
                        data-testid={`drawer-link-${key}`}
                      >
                        <span>{pick(it.label, lang)}</span>
                        <ArrowRight
                          className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                          strokeWidth={1.6}
                        />
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}

          {/* Contact block — moved inside the scrollable body so it
              scrolls with the rest of the menu content. Only the site
              preferences (language & currency) remain pinned in the
              fixed footer below. */}
          <section
            className="mt-4 pt-8 border-t border-[#2C2621]/10"
            data-testid="drawer-contact-block"
          >
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#C16542]">
              {pick({ en: "Contact", fr: "Contact", es: "Contacto" }, lang)}
            </p>
            <p className="mt-2 text-sm text-[#5C5248] leading-relaxed">
              {pick(
                {
                  en: "Reach a host directly — we answer every enquiry in person.",
                  fr: "Contactez un hôte directement — nous répondons à chaque demande en personne.",
                  es: "Contacta con un anfitrión directamente — respondemos cada solicitud en persona.",
                },
                lang,
              )}
            </p>
            <ul className="mt-5 space-y-3">
              <li>
                <a
                  href="tel:+212535578450"
                  className="flex items-center gap-3 text-sm text-[#2C2621] hover:text-[#C16542] transition-colors"
                  data-testid="drawer-contact-phone"
                >
                  <span className="w-9 h-9 rounded-full border border-[#2C2621]/15 flex items-center justify-center shrink-0">
                    <Phone className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.6} />
                  </span>
                  <span className="font-serif-x text-base leading-tight">+212 535 57 84 50</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:xaluca@xaluca.com"
                  className="flex items-center gap-3 text-sm text-[#2C2621] hover:text-[#C16542] transition-colors"
                  data-testid="drawer-contact-email"
                >
                  <span className="w-9 h-9 rounded-full border border-[#2C2621]/15 flex items-center justify-center shrink-0">
                    <Mail className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.6} />
                  </span>
                  <span className="font-serif-x text-base leading-tight">xaluca@xaluca.com</span>
                </a>
              </li>
              <li>
                <p
                  className="flex items-center gap-3 text-sm text-[#5C5248]"
                  data-testid="drawer-contact-location"
                >
                  <span className="w-9 h-9 rounded-full border border-[#2C2621]/15 flex items-center justify-center shrink-0">
                    <MapPin className="w-3.5 h-3.5 text-[#C16542]" strokeWidth={1.6} />
                  </span>
                  <span className="font-serif-x text-base leading-tight">
                    {pick(
                      {
                        en: "Erfoud · southern Morocco",
                        fr: "Erfoud · sud du Maroc",
                        es: "Erfoud · sur de Marruecos",
                      },
                      lang,
                    )}
                  </span>
                </p>
              </li>
            </ul>
            <NavLink
              to="/contact"
              className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#2C2621] hover:text-[#C16542] transition-colors"
              data-testid="drawer-contact-link"
            >
              {pick(
                { en: "Talk to a host", fr: "Parler à un hôte", es: "Habla con un anfitrión" },
                lang,
              )}
              <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
            </NavLink>
          </section>

          {/* Booking CTA — also lives inside the scrollable body per
              product direction (only Idioma + Moneda stay pinned). */}
          <Link
            to="/booking"
            className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] px-6 py-3.5 text-[11px] tracking-[0.3em] uppercase transition-colors"
            data-testid="drawer-book-cta"
          >
            {t("nav.book")}
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
          </Link>
        </div>

        {/* Drawer footer — pinned, contains ONLY the site preferences
            (Language & Currency) per product direction. Everything else
            has been moved inside the scrollable body above. */}
        <div className="px-7 md:px-10 py-5 border-t border-[#2C2621]/10 bg-white/40">
          <SitePreferences variant="light" testIdPrefix="drawer-preferences" />
        </div>
      </aside>
    </>
  );
};

export default Header;
